import { useRef } from 'react';
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';

import { Filters } from '../../models/Filters';
import { useExportStockMovements } from '../../hooks/useExportStockMovements';

interface Props {
  /** Todos los filtros (searchTerm, event_ids, sortBy, sortDirection, page, limit…) */
  filters: Filters;
}

export const DownloadExcelButton: React.FC<Props> = ({ filters }) => {
  const { t } = useTranslation('stockmovementsModule');
  const { toast } = useToast();
  const exportMutation = useExportStockMovements();
  const isExportingRef = useRef(false);

  const handleDownload = () => {
    // Doble protección: ref sincrónico + isPending para evitar ejecuciones duplicadas
    if (isExportingRef.current || exportMutation.isPending) return;
    isExportingRef.current = true;

    exportMutation.mutate(filters, {
      onSuccess: (url) => {
        if (url) {
          // Descarga única mediante enlace temporal (evita la doble descarga de window.open + fallback)
          const link = document.createElement('a');
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          toast({
            title: t('toasts.export_success'),
            description: t('buttons.download_excel'),
          });
        } else {
          toast({
            title: t('toasts.error_title'),
            description: 'No se pudo obtener la URL del archivo',
            variant: 'destructive',
          });
        }
      },
      onError: (error: any) => {
        toast({
          title: t('toasts.error_title'),
          description: error?.message ?? 'Error al generar el archivo Excel',
          variant: 'destructive',
        });
      },
      onSettled: () => {
        isExportingRef.current = false;
      },
    });
  };

  return (
    <Button
      onClick={handleDownload}
      disabled={exportMutation.isPending}
      data-testid="download-excel-button"
      type="button"
      size="sm"
      variant="outline"
      className={`!py-[9px] !px-2 !text-icons transition-opacity duration-200 ${
        exportMutation.isPending ? 'opacity-60 pointer-events-none' : ''
      }`}
    >
      <FontAwesomeIcon icon={faFileArrowDown} />
      <span className="hidden sm:inline-block ml-1">
        {exportMutation.isPending ? t('buttons.generating_excel') : t('buttons.download_excel')}
      </span>
    </Button>
  );
};
